import { useParams } from 'react-router-dom';
import ChatsMessagesList from './ChatsMessagesList';
import ChatsContentHeader from './ChatsContentHeader';
import { useInfiniteChatMessages, useMarkAllRead, useMyChats } from '@/queries/chats';
import { useEffect, useRef, useState } from 'react';
import { getBackUrl } from '@/utils/helpers';
import chatIcon from '@/assets/icons/chat-icon.svg';

const ChatsContent = () => {
  const { chatId } = useParams();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isFetchingOlderPageRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const normalizedSearchQuery = searchQuery || undefined;
  const resetKey = `${chatId ?? 'no-chat'}:${normalizedSearchQuery ?? ''}`;

  const { data: chats = [] } = useMyChats();
  const {
    data: messages = [],
    fetchNextPage,
    hasNextPage,
  } = useInfiniteChatMessages(chatId, normalizedSearchQuery);
  const { mutate: markAllRead } = useMarkAllRead();

  const chat = chats.find((c) => c.id === chatId);

  useEffect(() => {
    if (chatId) markAllRead(chatId);
    setSearchQuery('');
  }, [chatId, markAllRead]);

  useEffect(() => {
    isFetchingOlderPageRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    if (!hasNextPage) return;

    const maybeFetchOlder = () => {
      const distanceToBottom =
        scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
      if (distanceToBottom > 120) return;
      if (isFetchingOlderPageRef.current) return;
      if (!hasNextPage) return;

      isFetchingOlderPageRef.current = true;

      fetchNextPage().finally(() => {
        isFetchingOlderPageRef.current = false;
      });
    };

    maybeFetchOlder();
    scrollContainer.addEventListener('scroll', maybeFetchOlder, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', maybeFetchOlder);
    };
  }, [fetchNextPage, hasNextPage, messages.length]);

  return (
    <div id="chats-content" className="relative flex h-full w-full min-w-0 flex-col">
      <ChatsContentHeader
        title={chat?.title}
        type={chat?.type}
        iconSrc={chat?.upload?.path ? `${getBackUrl()}/uploads/${chat.upload.path}` : chatIcon}
        usersAmount={chat?.usersAmount}
        onSearch={setSearchQuery}
      />
      <div
        id="chats-messages-scroll"
        ref={scrollContainerRef}
        className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-lightGray md:bg-[#f3f3f5]"
      >
        <div className="flex min-h-full min-w-0 flex-col px-4 py-5 max-md:pb-28 md:px-5 md:py-3 xl:px-8">
          <ChatsMessagesList
            key={chatId ?? 'no-chat'}
            messages={messages}
            chatTitle={chat?.title}
            searchQuery={normalizedSearchQuery}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatsContent;
